import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, hasAdminPanelAccess } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useIsMobile } from '../../hooks/useMediaQuery';
import Button from '../Button';
import '@fortawesome/fontawesome-free/css/all.min.css';
import AppDropdown from 'components/AppDropdown';
import HamburgerMenu from './HamburgerMenu';
import MobileMenuSection from './MobileMenuSection';
import ProfilePicture from 'components/Profile/ProfilePicture';

/** Default avatar image when user has no profile picture (user silhouette SVG). */
const DEFAULT_AVATAR_DATA_URI =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23adb5bd"%3E%3Cpath d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/%3E%3C/svg%3E';

const Navbar: React.FC = () => {
  const { logout, user } = useAuth();
  const { unreadCount } = useNotifications();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  const desktopNav = user ? (
    <div className="nav-right d-flex align-items-center">
      <Link to="/ilan-ver" className="btn btn-outline-primary me-3">
        <i className="fas fa-plus-circle me-2"></i>İlan Ver
      </Link>
      <Link
        to="/notifications"
        className="btn btn-link text-primary position-relative border-0 me-3"
        style={{ textDecoration: 'none' }}
      >
        <i className="fas fa-bell fs-5"></i>
        {unreadCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-white text-primary" style={{ border: '1px solid #0d6efd' }}>
            {unreadCount}
          </span>
        )}
      </Link>
      <AppDropdown
        id="messagesDropdown"
        icon="fas fa-envelope fs-5"
      >
        <>
          <h6 className="dropdown-header bg-primary text-white">Mesajlar</h6>
          <div className="dropdown-divider"></div>
          <Link to="/mesajlar" className="dropdown-item text-center text-primary fw-semibold py-2">
            Tüm Mesajları Görüntüle
          </Link>
        </>
      </AppDropdown>
      <AppDropdown
        id="userDropdown"
        imgSrc={user?.profilePictureUrl ?? DEFAULT_AVATAR_DATA_URI}
        name={user?.name || 'Ahmet Yılmaz'}
      >
        <>
          <li className="px-3 py-3 border-bottom mb-2">
            <div className="d-flex align-items-center">
              <ProfilePicture width={50} height={50}  />
              <div>
                <h6 className="mb-1 fw-bold">{user?.name || 'Ahmet Yılmaz'}</h6>
                <p className="small text-muted mb-0">{user?.email || 'ahmet.yilmaz@example.com'}</p>
              </div>
            </div>
          </li>
          <li><Link className="dropdown-item py-2" to="/profil"><i className="fas fa-user me-2 text-primary"></i>Profilim</Link></li>
          <li><Link className="dropdown-item py-2" to="/ilanlarim"><i className="fas fa-list me-2 text-primary"></i>İlanlarım</Link></li>
          <li><Link className="dropdown-item py-2" to="/favoriler"><i className="fas fa-heart me-2 text-primary"></i>Favorilerim</Link></li>
          <li><Link className="dropdown-item py-2" to="/teknik-destek"><i className="fas fa-headset me-2 text-primary"></i>Destek Taleplerim</Link></li>
          {hasAdminPanelAccess(user) && (
            <li><Link className="dropdown-item py-2" to="/admin-panel"><i className="fas fa-shield-alt me-2 text-primary"></i>Admin Panel</Link></li>
          )}
          <li className="border-top mt-2">
            <button
              type="button"
              className="dropdown-item py-2 mt-2"
              onClick={logout}
            >
              <i className="fas fa-sign-out-alt me-2 text-danger"></i>Çıkış Yap
            </button>
          </li>
        </>
      </AppDropdown>
    </div>
  ) : (
    <div className="nav-right">
      <Link className="btn btn-outline-primary me-2" to="/giris">
        <i className="fas fa-plus-circle me-2" />
        İlan Ver
      </Link>
      <Link className="btn btn-primary me-2" to="/giris">
        <i className="fas fa-user me-2" />
        Giriş Yap
      </Link>
    </div>
  );

  const mobileMenuContent = user ? (
    <>
      <Link to="/ilan-ver" className="d-flex align-items-center py-3 text-dark text-decoration-none border-bottom">
        <i className="fas fa-plus-circle me-2 text-primary" />
        İlan Ver
      </Link>
      <Link to="/notifications" className="d-flex align-items-center py-3 text-dark text-decoration-none border-bottom position-relative">
        <i className="fas fa-bell me-2 text-primary" />
        Bildirimler
        {unreadCount > 0 && (
          <span className="badge rounded-pill bg-primary ms-2">{unreadCount}</span>
        )}
      </Link>
      <MobileMenuSection title={<><i className="fas fa-envelope me-2 text-primary" />Mesajlar</>}>
        <Link to="/mesajlar" className="dropdown-item py-2">
          <i className="fas fa-inbox me-2 text-primary" />
          Tüm Mesajları Görüntüle
        </Link>
      </MobileMenuSection>
      <MobileMenuSection title={<><i className="fas fa-user me-2 text-primary" />{user?.name || 'Hesabım'}</>}>
        <Link to="/profil" className="dropdown-item py-2"><i className="fas fa-user me-2 text-primary"></i>Profilim</Link>
        <Link to="/ilanlarim" className="dropdown-item py-2"><i className="fas fa-list me-2 text-primary"></i>İlanlarım</Link>
        <Link to="/favoriler" className="dropdown-item py-2"><i className="fas fa-heart me-2 text-primary"></i>Favorilerim</Link>
        <Link to="/teknik-destek" className="dropdown-item py-2"><i className="fas fa-headset me-2 text-primary"></i>Destek Taleplerim</Link>
        {hasAdminPanelAccess(user) && (
          <Link to="/admin-panel" className="dropdown-item py-2"><i className="fas fa-shield-alt me-2 text-primary"></i>Admin Panel</Link>
        )}
        <button
          type="button"
          className="dropdown-item py-2 mt-2 w-100 text-start border-0 bg-transparent"
          onClick={() => {
            logout();
            closeMenu();
          }}
        >
          <i className="fas fa-sign-out-alt me-2 text-danger"></i>Çıkış Yap
        </button>
      </MobileMenuSection>
    </>
  ) : (
    <>
      <Link to="/giris" className="d-flex align-items-center py-3 text-dark text-decoration-none border-bottom">
        <i className="fas fa-plus-circle me-2 text-primary" />
        İlan Ver
      </Link>
      <Link to="/giris" className="d-flex align-items-center py-3 text-dark text-decoration-none border-bottom">
        <i className="fas fa-user me-2 text-primary" />
        Giriş Yap
      </Link>
    </>
  );

  return (
    <header className="header sticky-top bg-white">
      <nav className="navbar navbar-expand-lg">
        <div className="container">
          {/* Logo */}
          <Link className="navbar-brand fw-bold text-primary" to="/">
            <i className="fas fa-laptop me-2"></i>
            <span className="d-none d-sm-inline">SadeceBilgisayar.com</span>
            <span className="d-inline d-sm-none">SadeceBilgisayar</span>
          </Link>

          {/* Search - visible on all sizes */}
          <div className="search-box mx-lg-4 flex-grow-1 order-lg-0">
            <form className="d-flex">
              <div className="input-group">
                <input
                  type="search"
                  className="form-control"
                  placeholder="Dizüstü, masaüstü, parça ara..."
                  aria-label="Arama"
                />
                <Button className="btn btn-primary" type="submit">
                  <i className="fas fa-search"></i>
                </Button>
              </div>
            </form>
          </div>

          {/* Desktop: right menu */}
          {!isMobile && desktopNav}

          {/* Mobile: hamburger */}
          {isMobile && (
            <Button
              type="button"
              className="btn btn-link text-dark p-2 ms-2"
              onClick={() => setMenuOpen(true)}
              aria-label="Menüyü aç"
            >
              <i className="fas fa-bars fa-lg" />
            </Button>
          )}
        </div>
      </nav>

      {isMobile && (
        <HamburgerMenu isOpen={menuOpen} onClose={closeMenu}>
          {mobileMenuContent}
        </HamburgerMenu>
      )}

      <style>
        {`
        /* Dropdown Styles */
        .dropdown-menu {
            box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
            animation: fadeIn 0.3s ease;
        }

        .dropdown-header {
            font-weight: 600;
            letter-spacing: 0.5px;
            border-bottom: 1px solid rgba(0, 0, 0, 0.05);
        }

        .dropdown-item {
            padding: 0.6rem 1.2rem;
            transition: all 0.2s;
        }

        .dropdown-item:hover {
            background-color: #f0f7ff;
            padding-left: 1.5rem;
        }

        .dropdown-item:active {
            background-color: #0d6efd;
            color: white;
        }

        .dropdown-item:active i {
            color: white !important;
        }

        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-10px); }
            to { opacity: 1; transform: translateY(0); }
        }

        .btn-link {
            text-decoration: none !important;
            box-shadow: none !important;
            border: none !important;
            outline: none !important;
        }

        .btn-link:focus, .btn-link:hover, .btn-link:active {
            text-decoration: none !important;
            box-shadow: none !important;
            border: none !important;
            outline: none !important;
        }

        .dropdown-toggle::after {
            vertical-align: middle;
            margin-left: 0.4em;
        }

        /* Mobile: keep logo + search + hamburger in one row */
        @media (max-width: 991px) {
          .navbar .container {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
          }
          .navbar-brand {
            flex-shrink: 0;
          }
          .search-box {
            flex: 1;
            min-width: 0;
            max-width: none;
          }
        }
        `}
      </style>
    </header>
  );
};

export default Navbar;
